import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { Check } from "lucide-react";
import { apiFetch } from "@/lib/reels";
import { PrimaryButton } from "@/components/reels/controls";
import { BulkResults, Thumb } from "@/components/reels/BulkResults";
import { useDownloadQueue } from "@/hooks/useDownloadQueue";

export const Tile = ({ m, selected, onToggle, i, testPrefix = "saved-tile", footer }) => (
  <div className="relative aspect-[9/16] overflow-hidden rounded-lg">
  <button className="absolute inset-0" onClick={onToggle} data-testid={`${testPrefix}-${i}`}>
    <Thumb src={m.thumb_data || m.thumbnail} className="h-full w-full !rounded-none" />
    <span className={`absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${selected ? "bg-[#d7352b]" : "bg-black/40"}`}>
      {selected && <Check size={13} className="text-white" />}
    </span>
  </button>
    {footer}
  </div>
);

export const SavedBrowser = ({ username, quality, onDisconnect }) => {
  const { getToken } = useAuth();
  const [cols, setCols] = useState([]);
  const [active, setActive] = useState("saved");
  const [media, setMedia] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const queue = useDownloadQueue(quality);

  const call = useCallback(async (path) => (await apiFetch(path, { token: await getToken() })).json(), [getToken]);

  const loadMedia = useCallback(async (id, after = "") => {
    setLoading(true);
    setError("");
    try {
      const data = await call(`/instagram/collections/${id}/media?cursor=${encodeURIComponent(after)}`);
      const videos = data.items.filter((m) => m.is_video);
      setMedia((prev) => (after ? [...prev, ...videos] : videos));
      setCursor(data.cursor);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => { call("/instagram/collections").then(setCols).catch((e) => setError(e.message)); }, [call]);
  useEffect(() => { setSelected(new Set()); setMedia([]); loadMedia(active); }, [active, loadMedia]);

  const toggle = (url) => setSelected((s) => { const n = new Set(s); n.has(url) ? n.delete(url) : n.add(url); return n; });
  const allSelected = media.length > 0 && selected.size === media.length;
  const picks = media.filter((m) => selected.has(m.url));

  return (
    <>
      <div className="rs-material flex flex-col gap-3 p-4 rs-fade" data-testid="saved-browser">
        <div className="flex items-center justify-between">
          <p className="text-[15px]" data-testid="ig-connected-username">@{username}</p>
          <button className="text-[13px] text-[#c62d22]" onClick={onDisconnect} data-testid="ig-disconnect-btn">Disconnect</button>
        </div>
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" data-testid="saved-collections">
          {(cols.length ? cols : [{ id: "saved", name: "All saved" }]).map((c) => (
            <button key={c.id} className="rs-chip" data-active={active === c.id} onClick={() => setActive(c.id)} data-testid={`saved-collection-${c.id}`}>
              {c.name}{c.count != null ? ` · ${c.count}` : ""}
            </button>
          ))}
        </div>
        {error && <p className="text-[13px] text-[#c62d22]" data-testid="saved-error">{error}</p>}
        <div className="flex items-center justify-between">
          <span className="rs-secondary text-[13px]" data-testid="saved-count">{media.length} videos · {selected.size} selected</span>
          <button className="rs-bordered text-[13px]" disabled={!media.length} data-testid="saved-select-all-btn"
            onClick={() => setSelected(allSelected ? new Set() : new Set(media.map((m) => m.url)))}>{allSelected ? "Deselect All" : "Select All"}</button>
        </div>
        <div className="grid max-h-[420px] grid-cols-3 gap-1.5 overflow-y-auto" data-testid="saved-grid">
          {media.map((m, i) => <Tile key={m.pk} m={m} i={i} selected={selected.has(m.url)} onToggle={() => toggle(m.url)} />)}
        </div>
        {loading && <div className="flex justify-center py-2"><span className="rs-spinner" /></div>}
        {cursor && !loading && <button className="rs-bordered" onClick={() => loadMedia(active, cursor)} data-testid="saved-load-more-btn">Load More</button>}
        <PrimaryButton testId="saved-download-btn" busy={queue.busy} disabled={queue.busy || !picks.length}
          onClick={() => queue.run(picks.map((m) => ({ url: m.url, thumbnail: m.thumbnail, caption: m.caption })))}
          label={queue.busy ? "Downloading…" : `Download ${picks.length || ""} Selected`.replace("  ", " ")} />
      </div>
      {queue.items.length > 0 && <BulkResults items={queue.items} busy={queue.busy} />}
    </>
  );
};
